<template>
  <div class="num-vfx">
    <canvas
      :id="canvasID"
      class="canvas1"></canvas>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import useUtils from '@/composables/useUtils'

const { genUUID } = useUtils()

const canvasID = `canvas-${genUUID()}`

const props = defineProps({
  color: {
    type: String,
    default: 'white',
  },
})

onMounted(() => {
  const canvas = document.getElementById(canvasID)
  const ctx = canvas.getContext('2d')
  canvas.width = 1000
  canvas.height = 1000

  class Symbol {
    constructor(x, y, fontSize, canvasHeight) {
      this.characters = ' 000111.|'
      this.x = x
      this.y = y
      this.fontSize = fontSize
      this.text = 'A'
      this.canvasHeight = canvasHeight
    }
    draw(context) {
      this.text = this.characters.charAt(
        Math.floor(Math.random() * this.characters.length)
      )
      context.save() // Save the current context state
      // Translate to the position where the symbol will be drawn, adding half fontSize to center the rotation
      context.translate(
        this.x * this.fontSize + this.fontSize / 2,
        this.y * this.fontSize + this.fontSize / 2
      )
      context.rotate(-Math.PI / 2) // Rotate the context 90 degrees clockwise
      context.fillStyle = props.color
      // Draw the text offset by half fontSize to align it correctly after rotation
      context.fillText(this.text, -this.fontSize / 2, this.fontSize / 2)
      context.restore() // Restore the context to its original state

      // Vertical movement logic remains the same
      if (this.y * this.fontSize > this.canvasHeight && Math.random() > 0.997) {
        this.y = 0
      } else {
        this.y += 0.9
      }
    }
  }

  class Effect {
    constructor(canvasWidth, canvasHeight) {
      this.fontSize = 14
      this.canvasWidth = canvasWidth
      this.canvasHeight = canvasHeight
      this.columns = this.canvasWidth / this.fontSize
      this.symbols = []
      this.initialize()
    }
    initialize() {
      function getRandomArbitrary(min, max) {
        return Math.random() * (max - min) + min
      }
      for (let i = 0; i < this.columns; i++) {
        let startY
        if (Math.random() > 0.5) {
          // 50% chance to start normally
          startY = -Math.floor(
            Math.random() * (this.canvasHeight / this.fontSize)
          )
        } else {
          // 50% chance for a delayed start
          startY = -(
            getRandomArbitrary(2, 4) *
            (Math.floor(Math.random() * (this.canvasHeight / this.fontSize)) +
              this.canvasHeight / this.fontSize)
          )
        }
        this.symbols[i] = new Symbol(
          i,
          startY,
          this.fontSize,
          this.canvasHeight
        )
      }
    }

    resize(width, height) {
      this.canvasWidth = width
      this.canvasHeight = height
      this.columns = this.canvasWidth / this.fontSize
      this.symbols = []
      this.initialize()
    }
  }

  const effect = new Effect(canvas.width, canvas.height)
  let lastTime = 0
  const fps = 60
  const nextFrame = 1000 / fps
  let timer = 0

  function animate(timeStamp) {
    const deltaTime = timeStamp - lastTime
    lastTime = timeStamp
    if (timer > nextFrame) {
      ctx.globalCompositeOperation = 'destination-out' // Set to 'destination-out' to apply the fade effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)' // Semi-transparent black will gradually clear the canvas
      ctx.fillRect(0, 0, canvas.width, canvas.height) // Apply the fade effect
      ctx.globalCompositeOperation = 'source-over' // Reset to default to draw new symbols
      ctx.textAlign = 'right'
      ctx.font = effect.fontSize + 'px monospace'
      effect.symbols.forEach((symbol) => symbol.draw(ctx))
      timer = 0
    } else {
      timer += deltaTime
    }

    requestAnimationFrame(animate)
  }

  animate(0)

  window.addEventListener('resize', function () {
    canvas.width = 1000
    canvas.height = 1000
    effect.resize(canvas.width, canvas.height)
  })
})
</script>

<style lang="scss">
.num-vfx {
  @apply relative w-full h-full;

  .canvas1 {
    transform: rotate(0deg) translateY(0%);

    transform: rotate(90deg);
    position: absolute;
    opacity: 0.5;
    top: 0;
    left: 0;
  }
}
</style>
